import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import { Box, Typography } from "@mui/material";
import i18n from "../i18n";

const alignIconsMap: Record<string, React.JSX.Element> = {
	"": <FormatAlignLeftIcon fontSize="small" className="align-icon" />,
	center: <FormatAlignCenterIcon fontSize="small" className="align-icon" />,
	right: <FormatAlignRightIcon fontSize="small" className="align-icon" />,
	justify: <FormatAlignJustifyIcon fontSize="small" className="align-icon" />,
};

export const fontSizes: { label: string; value: FormatValueMap["size"] }[] = [
	{ label: i18n.t("instructionEditorTSX.fontSize.small"), value: "small" },
	{ label: i18n.t("instructionEditorTSX.fontSize.normal"), value: "" },
	{ label: i18n.t("instructionEditorTSX.fontSize.large"), value: "large" },
	{ label: i18n.t("instructionEditorTSX.fontSize.huge"), value: "huge" },
];

// Heading Levels - Value in number because that is how react quill expects it for headings
export const headingOptions: { label: string; value: FormatValueMap["header"] }[] = [
	{ label: i18n.t("instructionEditorTSX.heading.h1"), value: 1 },
	{ label: i18n.t("instructionEditorTSX.heading.h2"), value: 2 },
	{ label: i18n.t("instructionEditorTSX.heading.h3"), value: 3 },
	{ label: i18n.t("instructionEditorTSX.heading.h4"), value: 4 },
	{ label: i18n.t("instructionEditorTSX.heading.h5"), value: 5 },
	{ label: i18n.t("instructionEditorTSX.heading.h6"), value: 6 },
	{ label: i18n.t("instructionEditorTSX.heading.normal"), value: "" },
];

// Default react-quill colors
export const textColors: { label: string; value: FormatValueMap["color"] }[] = [
	{ label: "#000000", value: "#000000" },
	{ label: "#e60000", value: "#e60000" },
	{ label: "#ff9900", value: "#ff9900" },
	{ label: "#ffff00", value: "#ffff00" },
	{ label: "#008a00", value: "#008a00" },
	{ label: "#0066cc", value: "#0066cc" },
	{ label: "#9933ff", value: "#9933ff" },
	{ label: "#ffffff", value: "#ffffff" },
	{ label: "#facccc", value: "#facccc" },
	{ label: "#ffebcc", value: "#ffebcc" },
	{ label: "#ffffcc", value: "#ffffcc" },
	{ label: "#cce8cc", value: "#cce8cc" },
	{ label: "#cce0f5", value: "#cce0f5" },
	{ label: "#ebd6ff", value: "#ebd6ff" },
	{ label: "#bbbbbb", value: "#bbbbbb" },
	{ label: "#f06666", value: "#f06666" },
	{ label: "#ffc266", value: "#ffc266" },
	{ label: "#ffff66", value: "#ffff66" },
	{ label: "#66b966", value: "#66b966" },
	{ label: "#66a3e0", value: "#66a3e0" },
	{ label: "#c285ff", value: "#c285ff" },
	{ label: "#888888", value: "#888888" },
	{ label: "#a10000", value: "#a10000" },
	{ label: "#b26b00", value: "#b26b00" },
	{ label: "#b2b200", value: "#b2b200" },
	{ label: "#006100", value: "#006100" },
	{ label: "#0047b2", value: "#0047b2" },
	{ label: "#6b24b2", value: "#6b24b2" },
	{ label: "#444444", value: "#444444" },
	{ label: "#5c0000", value: "#5c0000" },
	{ label: "#663d00", value: "#663d00" },
	{ label: "#666600", value: "#666600" },
	{ label: "#003700", value: "#003700" },
	{ label: "#002966", value: "#002966" },
	{ label: "#3d1466", value: "#3d1466" },
];

// Font Families
export const fontFamilies: { label: string; value: FormatValueMap["font"] }[] = [
	{ label: i18n.t("instructionEditorTSX.font.sansSerif"), value: "" },
	{ label: i18n.t("instructionEditorTSX.font.serif"), value: "serif" },
	{ label: i18n.t("instructionEditorTSX.font.monospace"), value: "monospace" },
];

// Text Alignment
export const alignOptions: { label: string; value: FormatValueMap["align"] }[] = [
	{ label: i18n.t("instructionEditorTSX.align.left"), value: "" },
	{ label: i18n.t("instructionEditorTSX.align.center"), value: "center" },
	{ label: i18n.t("instructionEditorTSX.align.right"), value: "right" },
	{ label: i18n.t("instructionEditorTSX.align.justify"), value: "justify" },
];

// Render function for each color square
export function renderColorSquare(value: FormatValueMap["color"], _label: string) {
	return (
		<Box
			sx={{
				width: 16,
				height: 16,
				backgroundColor: value,
				borderRadius: "2px",
				boxSizing: "border-box",
				border: "1px solid transparent",
				"&:hover": {
					borderColor: "#000",
				},
				"&:focus-visible": {
					outline: "2px solid var(--focus-outline-color-dark)",
					outlineOffset: 2,
				},
			}}
		/>
	);
}

// MenuProps override to show 5×7 grid instead of vertical list
export const colorMenuProps = {
	PaperProps: {
		sx: {
			"& .MuiMenu-list": {
				display: "grid",
				gridTemplateColumns: "repeat(7, 1fr)",
				gap: "2px",
				padding: "4px",
			},
			"& .MuiMenuItem-root": {
				padding: "2px",
				minHeight: 0,
				justifyContent: "center",
				"&:hover": {
					backgroundColor: "transparent",
				},
			},
		},
	},
};

export function renderSizeOption(value: string, label: string) {
	let sxFontSize: string;
	switch (value) {
		case "small":
			sxFontSize = "10px";
			break;
		case "":
			sxFontSize = "14px";
			break;
		case "large":
			sxFontSize = "18px";
			break;
		case "huge":
			sxFontSize = "32px";
			break;
		default:
			sxFontSize = "14px";
	}
	return <Typography sx={{ fontSize: sxFontSize }}>{label}</Typography>;
}

export function renderHeaderOption(value: string | number, label: string) {
	if (value === "") {
		return <Typography sx={{ fontSize: "14px" }}>{label}</Typography>;
	}

	const sizeMap: Record<number, string> = {
		1: "28px",
		2: "21px",
		3: "16.38px",
		4: "14px",
		5: "11.62px",
		6: "9.38px",
	};
	const px = sizeMap[value as number] || "14px";

	return <Typography sx={{ fontSize: px, lineHeight: 1.2 }}>{label}</Typography>;
}

export function renderFontOption(value: string, label: string) {
	const fontFamily =
		value === "sans"
			? "Arial, Helvetica, sans-serif"
			: value === "serif"
				? "Georgia, serif"
				: value === "mono"
					? "Courier New, monospace"
					: value || "inherit";

	return (
		<Typography
			sx={{
				fontFamily,
				fontSize: 14,
				fontWeight: 500,
			}}
		>
			{label}
		</Typography>
	);
}

export function renderAlignOption(value: string, _label: string) {
	return <span style={{ display: "flex", alignItems: "center", gap: 6 }}>{alignIconsMap[value] || null}</span>;
}

export function renderAlignValue(value: string) {
	return <span>{alignIconsMap[value] || alignIconsMap[""]}</span>;
}

function getContrastColor(bgColor: string): string {
	// Strip hash if present
	const hex = bgColor.replace("#", "");

	// Expand shorthand (#abc -> #aabbcc)
	const fullHex =
		hex.length === 3
			? hex
					.split("")
					.map((c) => c + c)
					.join("")
			: hex;

	const r = parseInt(fullHex.slice(0, 2), 16);
	const g = parseInt(fullHex.slice(2, 4), 16);
	const b = parseInt(fullHex.slice(4, 6), 16);

	// Calculate luminance
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

	return luminance < 0.5 ? "#fff" : "#000";
}

export function renderBackgroundValue(value: string): React.ReactNode {
	const textColor = value ? getContrastColor(value) : "#000";

	return (
		<span
			style={{
				display: "inline-block",
				padding: "0 6px",
				fontWeight: "bold",
				fontSize: 14,
				backgroundColor: value,
				color: textColor,
				border: "1px solid #ccc",
				borderRadius: 2,
			}}
			aria-hidden="true"
		>
			A
		</span>
	);
}

export function renderColorValue(value: string) {
	return (
		<span
			style={{
				position: "relative",
				fontWeight: "bold",
				fontSize: 14,
				color: "#000",
				padding: "0 6px",
				display: "inline-block",
			}}
			aria-hidden="true"
		>
			A
			<span
				style={{
					position: "absolute",
					left: 0,
					bottom: 2,
					width: "100%",
					height: "2px",
					backgroundColor: value || "#000",
				}}
			/>
		</span>
	);
}

export type Format = keyof FormatValueMap;

export type FormatValueMap = {
	size: "small" | "" | "large" | "huge";
	header: 1 | 2 | 3 | 4 | 5 | 6 | "";
	// Quill Snow’s default 35‐color palette (5×7 grid)
	color:
		| "#000000"
		| "#e60000"
		| "#ff9900"
		| "#ffff00"
		| "#008a00"
		| "#0066cc"
		| "#9933ff"
		| "#ffffff"
		| "#facccc"
		| "#ffebcc"
		| "#ffffcc"
		| "#cce8cc"
		| "#cce0f5"
		| "#ebd6ff"
		| "#bbbbbb"
		| "#f06666"
		| "#ffc266"
		| "#ffff66"
		| "#66b966"
		| "#66a3e0"
		| "#c285ff"
		| "#888888"
		| "#a10000"
		| "#b26b00"
		| "#b2b200"
		| "#006100"
		| "#0047b2"
		| "#6b24b2"
		| "#444444"
		| "#5c0000"
		| "#663d00"
		| "#666600"
		| "#003700"
		| "#002966"
		| "#3d1466";

	background:
		| "#000000"
		| "#e60000"
		| "#ff9900"
		| "#ffff00"
		| "#008a00"
		| "#0066cc"
		| "#9933ff"
		| "#ffffff"
		| "#facccc"
		| "#ffebcc"
		| "#ffffcc"
		| "#cce8cc"
		| "#cce0f5"
		| "#ebd6ff"
		| "#bbbbbb"
		| "#f06666"
		| "#ffc266"
		| "#ffff66"
		| "#66b966"
		| "#66a3e0"
		| "#c285ff"
		| "#888888"
		| "#a10000"
		| "#b26b00"
		| "#b2b200"
		| "#006100"
		| "#0047b2"
		| "#6b24b2"
		| "#444444"
		| "#5c0000"
		| "#663d00"
		| "#666600"
		| "#003700"
		| "#002966"
		| "#3d1466";
	font: string;
	align: "" | "center" | "right" | "justify";

	bold: boolean;
	italic: boolean;
	underline: boolean;
	strike: boolean;

	blockquote: boolean;
	"code-block": boolean;

	link: string | boolean;
	image: string | boolean;
	video: string | boolean;

	list: "ordered" | "bullet" | "check";
	script: "super" | "sub";
	indent: number;
	direction: "rtl" | "ltr";

	clean: boolean;
};
